import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { CLIPS as ALLIES } from './allies.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Kaela of the Moss (models/kaela.ts, docs/ANTIHEROES.md): on Ser Corvin's bones, so she walks, reels, staggers and
 * falls as the fallen sentinel does. Her own: the thorn shot (the staff thrust at a hero, landing at 0.13 s), the
 * thorns (the staff struck on the ground, the knot of vines flung low), the pack (head thrown back, the left hand
 * raised to call), the channel of the Forest (the staff held high in both hands, shaking) and the Forest itself (the
 * staff driven into the ground). Her bear (`kaelaBear`) moves as the druid's bear, with a reared channel of its own
 * and its fall as her 'die'.
 *
 * Conventions as in corvin.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on the
 * trunk leans it forward, +Y turns the trunk to its left.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** The staff drawn back, the head turned to the hero: the shot's wind-up. */
const DRAWN: P = {
  body: [-0.04, -0.15, 0], chest: [-0.08, -0.3, 0], head: [0, 0.3, 0],
  armR: [-1.2, 0, -0.25], foreR: [-0.9, 0, 0], sword: [0.6, 0, 0], armL: [-0.4, 0, 0.2], foreL: [-0.5, 0, 0],
  legL: [-0.15, 0, 0], legR: [0.2, 0, 0], shinR: [0.25, 0, 0],
};
/** The staff thrust forward, its crook levelled at the hero (the thorn flies). */
const THRUST: P = {
  root: [0, -0.06, 0, 0, -0.03, 0.08], body: [0.12, 0.2, 0], chest: [0.15, 0.3, 0], head: [0.05, -0.25, 0],
  armR: [-1.45, 0, 0.05], foreR: [0.0, 0, 0], sword: [-1.2, 0, 0], armL: [0.2, 0, 0.25],
  legL: [-0.4, 0, 0], shinL: [0.35, 0, 0], legR: [0.3, 0, 0], shinR: [0.1, 0, 0], flapF: [-0.2, 0, 0], flapB: [0.2, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.0, false, (b, t) => {
    timeline(b, t, [
      [0, DRAWN],
      [0.05, DRAWN, outQuad],
      [0.13, THRUST, inCubic],
      [0.4, THRUST, outCubic],
      [1.0, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.13, 0.02]]), 'Espina');

/** Thorns: the staff struck on the ground before her, the knot of vines flung low toward the heroes. */
const thorns = (): Clip<B> =>
  clip<B>('thorns', 1.3, false, (b, t) => {
    const up: P = { chest: [-0.15, 0.2, 0], head: [-0.1, 0, 0], armR: [-2.2, 0, -0.2], foreR: [-0.4, 0, 0], sword: [0.2, 0, 0], armL: [-0.3, 0, 0.5], foreL: [-0.6, 0, 0] };
    const down: P = {
      root: [0, -0.1, 0, 0, -0.08, 0.06], body: [0.2, 0, 0], chest: [0.3, -0.2, 0], head: [0.15, 0, 0],
      armR: [-0.9, 0, 0.1], foreR: [-0.1, 0, 0], sword: [0.5, 0, 0], armL: [-1.3, 0, -0.5], foreL: [0.0, 0, 0], shield: [-0.4, 0, 0],
      legL: [-0.45, 0, 0], shinL: [0.55, 0, 0], legR: [0.35, 0, 0], shinR: [0.3, 0, 0], flapF: [-0.3, 0, 0], flapB: [0.3, 0, 0],
    };
    timeline(b, t, [
      [0, {}],
      [0.3, up, outQuad],
      [0.45, down, inCubic],
      [0.85, down],
      [1.3, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.45, 0.05]]), 'Espinas');

/** The pack: the head thrown back to the sky, the left hand raised to call them. */
const pack = (): Clip<B> =>
  clip<B>('pack', 1.6, false, (b, t) => {
    const call: P = {
      root: [0, 0.04, 0, 0, 0, 0], body: [-0.1, 0, 0], chest: [-0.25, 0.1, 0], head: [-0.6, 0, 0],
      armL: [-2.7, 0, 0.3], foreL: [-0.2, 0, 0], shield: [0.3, 0, 0], armR: [-0.3, 0, -0.2], foreR: [-0.3, 0, 0],
      legL: [-0.1, 0, -0.05], legR: [0.1, 0, 0.05],
    };
    timeline(b, t, [
      [0, {}],
      [0.3, call, outExpo],
      [1.1, { ...call, head: [-0.7, 0, 0] }],
      [1.6, {}, inOut],
    ], { head: 0.05 });
    const shake = t > 0.3 && t < 1.1 ? Math.sin(t * TAU * 8) * 0.03 : 0;
    b.head.rot(0, shake, 0);
  }, undefined, 'Manada');

/** The Forest, channelled: the staff held high in both hands over her head, shaking with it. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.03;
    b.root.pos(0, 0.03 + 0.015 * Math.sin(p * 2), 0).rot(-0.05, 0, 0);
    b.body.rot(-0.12, 0, 0);
    b.chest.rot(-0.25 + shake, 0.04 * Math.sin(p), 0);
    b.head.rot(-0.45, 0, 0);
    b.armR.rot(-2.85 + shake, 0, 0.25);
    b.foreR.rot(-0.2);
    b.sword.rot(-0.1 + shake * 2);
    b.armL.rot(-2.75 - shake, 0, -0.25);
    b.foreL.rot(-0.35);
    b.legL.rot(-0.2, 0, -0.1);
    b.shinL.rot(0.2);
    b.legR.rot(0.2, 0, 0.1);
    b.shinR.rot(0.25);
    b.flapF.rot(0.12 * Math.sin(p * 3));
    b.flapB.rot(-0.12 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.006;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The Forest comes: from high overhead, the staff driven down into the ground before her. */
const forest = (): Clip<B> =>
  clip<B>('forest', 1.6, false, (b, t) => {
    const high: P = { root: [0, 0.03, 0, 0, 0, 0], chest: [-0.25, 0, 0], head: [-0.45, 0, 0], armR: [-2.85, 0, 0.25], foreR: [-0.2, 0, 0], armL: [-2.75, 0, -0.25], foreL: [-0.35, 0, 0], legL: [-0.2, 0, -0.1], legR: [0.2, 0, 0.1] };
    const down: P = {
      root: [0, 0, 0, 0, -0.22, 0.18], body: [0.35, 0, 0], chest: [0.4, 0, 0], head: [0.25, 0, 0],
      armR: [-1.0, 0, 0.15], foreR: [-0.1, 0, 0], sword: [0.4, 0, 0], armL: [-1.0, 0, -0.15], foreL: [-0.3, 0, 0],
      legL: [-0.75, 0, -0.05], shinL: [0.95, 0, 0], legR: [0.5, 0, 0.05], shinR: [0.6, 0, 0], flapF: [-0.5, 0, 0], flapB: [0.45, 0, 0],
    };
    timeline(b, t, [
      [0, high],
      [0.18, down, inCubic],
      [0.8, down],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.18, 0.08]]), 'El bosque llega');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with her own shot and powers in place of the bash. */
export const kaelaClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), thorns(), pack(), channel(), forest()] as Clip[];

/** Her bear: the druid's bear's clips, its fall as her death, and a reared, bellowing channel. */
export const kaelaBearClips = (): Clip[] => {
  const base = (ALLIES.bear as () => Clip[])();
  const by = (n: string): Clip | undefined => base.find((c) => c.name === n);
  const down = by('down');
  const roar = by('roar');
  const out = [...base];
  if (down) out.push({ ...down, name: 'die', label: 'Cae' });
  if (roar) {
    out.push({
      name: 'channel',
      dur: 1.2,
      loop: true,
      // Held up on its hind legs at the height of the bellow, trembling (seamless: the tremor fits the loop).
      pose: (b, t) => roar.pose(b, 0.85 + 0.12 * (0.5 + 0.5 * Math.sin(phase(t, 1.2) * TAU * 2))),
      label: 'Canaliza',
    });
    out.push({ ...roar, name: 'forest', label: 'El bosque llega' });
  }
  return out;
};

export const CLIPS: Record<string, () => Clip[]> = { kaela: kaelaClips, kaelaBear: kaelaBearClips };
